import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { grooveNotes, type Groove } from "@web-shared/lib/groove";
import { isBoolean, usePref } from "@web-shared/hooks/usePref";
import { ChevronDown, Ghost, Grid3x3, Keyboard, Paintbrush, Pencil, X, ZoomIn, ZoomOut } from "lucide-react";
import type { WireNote } from "@shared/midi/notes.js";
import { clipColor, type LaneClip } from "@web-shared/lib/arrangement";
import {
  MIN_NOTE_BEATS,
  centerPitch,
  deleteNotes,
  dragNotes,
  dragNotesPlaced,
  duplicateNotes,
  isBlackKey,
  noteKey,
  notesWithin,
  OCTAVE_MAX,
  OCTAVE_MIN,
  pitchForKey,
  pitchName,
  playedNote,
  arpeggiateNotes,
  CHORDS,
  chopNotes,
  copyDragNotes,
  flamNotes,
  flipNotes,
  foldToScale,
  glueNotes,
  notesAtPitch,
  paintNote,
  pasteNotes,
  scaleLength,
  scaleVelocity,
  stampChord,
  strumNotes,
  humanizeNotes,
  legatoNotes,
  quantizeNotes,
  rampVelocity,
  reverseNotes,
  stepInsert,
  setNoteVelocity,
  setNotesVelocity,
  setNotesProbability,
  setNotesDeviation,
  toggleNotesMuted,
  transposeNotes,
} from "@web-shared/lib/piano-roll";
import { beats } from "@web-shared/lib/format";
import type { GhostNote } from "@web-shared/lib/ghost-notes";
import { inScale, ROOTS, SCALES, scaleById } from "@web-shared/lib/scales";
import { StepGrid } from "@web-shared/components/piano-roll/StepGrid";
import { drumLabel, type DrumNames } from "@web-shared/lib/step-grid";
import { cn } from "@web-shared/lib/utils";
import { Badge } from "@web-shared/components/ui/badge";
import { BigChoice, EditorTabs, Field, Pad, PadRow, Stepper } from "@web-shared/components/clip-editor/chrome";

/** Humanize ranges: ±a 64th in time, ±10 in velocity — audible, not sloppy. */
const HUMANIZE_BEATS = 1 / 16;
const HUMANIZE_VELOCITY = 10;

/** Strum: a 64th between a chord's notes — heard as one stroke, not an arpeggio. */
const STRUM_BEATS = 1 / 16;
/** Flam: the grace note a 64th ahead. */
const FLAM_BEATS = 1 / 16;
/** The note clipboard — one per page, so a phrase copied from one clip
 * pastes into another (FL pastes across patterns; Live across clips). */
let noteClipboard: WireNote[] = [];

const ROW_H = 12;
const KEYS_W = 44;
/** A kit's keys carry drum names ("Closed hat", "Tambourine"), so they are wider. */
const KIT_KEYS_W = 72;
const RULER_H = 18;
const VEL_H = 48;
const ROLL_H = 320;
/** Grab zone for an end resize, in px at a note's right edge. */
const EDGE_PX = 5;
/** Below this a drag is a click that wobbled, not a move. */
const DRAG_SLOP_PX = 3;
/** Default beat grid new notes snap to and drags move by — a 16th at 4/4. */
const DEFAULT_GRID = 0.25;
const GRID_CHOICES: Array<{ value: number; label: string }> = [
  { value: 0.125, label: "1/32" },
  { value: 0.25, label: "1/16" },
  { value: 1 / 6, label: "1/16T" },
  { value: 0.5, label: "1/8" },
  { value: 1 / 3, label: "1/8T" },
  { value: 1, label: "1/4" },
];

interface Drag {
  mode: "move" | "end";
  /** A clone drag (⌥ or ⇧ held at the press): the originals stay, copies move. */
  copy: boolean;
  keys: Set<string>;
  deltaBeats: number;
  deltaPitch: number;
  moved: boolean;
}

interface Props {
  clip: LaneClip;
  /** Present ⇒ notes can be drawn, dragged, resized and deleted; the whole
   * next note list is handed back on every completed gesture. */
  onChange?: (notes: WireNote[]) => void;
  /** Sound one note through the sine preview — feedback for add and select. */
  /** A note to hear now. May hand back its release — called when the key
   * that sounded it comes up — so a held key sounds for as long as it is held. */
  onAudition?: (note: WireNote) => void | (() => void);
  /** Proposed to the room but not yet confirmed. */
  pending?: boolean;
  /** The transport's position in CLIP-RELATIVE beats while it is running
   * through this clip; undefined otherwise. Drawn as a playhead, and what
   * keyboard input records against. */
  playheadBeats?: number;
  /** The playhead wraps round the clip (a launched slot clip): a note held
   * across the wrap is one note to the clip's end, not a lifted one. */
  looping?: boolean;
  /** Quarter-note beats per bar, for the heavier bar lines (4 at 4/4). */
  beatsPerBar?: number;
  /** Live's ×2 / ÷2: the clip doubles (notes repeating) or halves. Absent ⇒ hidden. */
  onScaleLoop?: (factor: 2 | 0.5) => void;
  /** The Set's scale as the room holds it (slice 44) — followed when
   * present: the rows outside it tint, Fold snaps to it, the local picker
   * steps aside. Live's Scale Mode off is shown as such. */
  roomScale?: { root: number; name: string; mode: boolean; intervals: readonly number[] };
  /** The lane plays the drum kit: the step view opens by default with the kit's rows. */
  kit?: boolean;
  /** A kit's own pad names by pitch, where known (lib/step-grid.ts
   * `drumLabel`) — the rows and keys say them in place of General MIDI's. */
  drumNames?: DrumNames;
  /** The other MIDI lanes' notes under this clip, in its beats (lib/ghost-notes.ts)
   * — FL Studio's ghost channels. Drawn dim behind the clip's own, never
   * editable; the toggle hides them. Absent ⇒ no toggle. */
  ghostNotes?: readonly GhostNote[];
  /** The grid's visible height in px (it scrolls past that). */
  rollHeight?: number;
  /** Fill the box it is in instead: the grid takes whatever height the
   * toolbar and the lanes leave (a phone's sheet, at any snap). */
  fillBox?: boolean;
  /** Open in draw mode — a touch screen's way to add notes, where a
   * double-click is not on offer. */
  startDrawing?: boolean;
  /** The outer frame's classes (a sheet drops the card border). */
  className?: string;
  onClose: () => void;
  /** Fold the roll down to the page's one-line strip, clip still loaded. */
  onMinimize?: () => void;
  /** The room's clip editor frame (MidiClipEditor): the audio clip editor's
   * strip and tabs around the grid instead of the roll's own toolbar. */
  editor?: RollEditor;
}

/** What the room's frame hands the roll (clip-editor/chrome.tsx). */
export interface RollEditor {
  /** Docked under the timeline (tabs and controls beside the grid), or the
   * phone's sheet (tabs over it, controls under it). */
  presentation: "docked" | "sheet";
  /** The clip's strip, docked (EditorHeader); the page draws it in a sheet. */
  header?: ReactNode;
  /** The Clip tab (ClipPanel); absent ⇒ no Clip tab. */
  clipPanel?: ReactNode;
}

type EditorTab = "notes" | "edit" | "scale" | "clip";
const isEditorTab = (v: unknown): v is EditorTab => v === "notes" || v === "edit" || v === "scale" || v === "clip";

/** The lane under the grid: which per-note value its bars show and drag. */
type LaneKind = "velocity" | "chance" | "deviation";
const LANE_KINDS: Array<{ kind: LaneKind; label: string; title: string }> = [
  { kind: "velocity", label: "vel", title: "Velocity lane — drag a bar to set it (a selected bar sets the selection)" },
  { kind: "chance", label: "%", title: "Chance lane — how often the note plays, 0–100% (Live 11's probability); the preview rolls it too" },
  { kind: "deviation", label: "±", title: "Velocity deviation lane — a random amount up to this is added to the velocity on each play (below the middle subtracts)" },
];

/** How long typed notes wait before being proposed — one intent per burst,
 * not one per key (each would stale the next's baseRev). */
const TYPE_FLUSH_MS = 700;

/**
 * A MIDI clip's notes on a pitch × beat grid — the picture Live's own MIDI
 * editor shows. All 128 pitches exist (scrolled to the content on open) so a
 * drag can go anywhere a note can; times are clip-relative beats, exactly the
 * wire's frame, so what's drawn is what `setClipNotes` proposes.
 */
export function PianoRoll({
  clip,
  onChange,
  onAudition,
  pending = false,
  playheadBeats,
  looping = false,
  beatsPerBar = 4,
  onScaleLoop,
  roomScale,
  kit = false,
  drumNames,
  ghostNotes,
  rollHeight = ROLL_H,
  fillBox = false,
  startDrawing = false,
  className,
  onClose,
  onMinimize,
  editor,
}: Props) {
  const [editorTab, setEditorTab] = usePref<EditorTab>("seedbed.midiEditor.tab", "notes", isEditorTab);
  const committed = useMemo(() => clip.notes ?? [], [clip.notes]);
  // Computer-keyboard input. Typed notes sit in `take` — drawn dashed — until
  // a pause in typing flushes them as ONE note-list proposal; `notes` below
  // is what every other gesture sees, so a drag mid-take keeps the take.
  const [kbd, setKbd] = useState(false);
  /** Draw mode (B): a press on empty grid draws a note and drags its length. */
  const [drawMode, setDrawModeState] = useState(startDrawing);
  /** Paint mode (P) — FL's brush: a sweep lays a note of the remembered
   * length at every grid cell it crosses. */
  const [paintMode, setPaintModeState] = useState(false);
  const setDrawMode = (next: boolean | ((d: boolean) => boolean)) => {
    const v = typeof next === "function" ? next(drawMode) : next;
    setDrawModeState(v);
    if (v) setPaintModeState(false);
  };
  const setPaintMode = (next: boolean | ((d: boolean) => boolean)) => {
    const v = typeof next === "function" ? next(paintMode) : next;
    setPaintModeState(v);
    if (v) setDrawModeState(false);
  };
  /** FL's note-length memory: a new note is as long as the last one drawn
   * or resized, not always one grid cell. Resets to the grid when it changes. */
  const [noteLength, setNoteLength] = useState(DEFAULT_GRID);
  /** FL's chord Stamp: what a draw places on the pressed pitch — the note
   * alone, or a chord built on it. */
  const [chordId, setChordId] = usePref<string>("seedbed.midiEditor.chord", "", isString);
  /** Ghost notes shown (when the page hands any). */
  const [ghosts, setGhosts] = usePref("seedbed.midiEditor.ghosts", true, isBoolean);
  /** A right-button sweep: the notes it has crossed are gone on release. */
  const [erasing, setErasing] = useState<Set<string> | null>(null);
  const eraseRef = useRef<Set<string> | null>(null);
  /** The step view (components/StepGrid.tsx) — a drum machine over the same
   * notes; on by default for a kit lane, remembered per browser otherwise. */
  const [stepsPref, setStepsPref] = usePref("seedbed-viz.rollSteps", false, isBoolean);
  const [stepsOverride, setStepsOverride] = useState<boolean | null>(null);
  const steps = stepsOverride ?? (kit || stepsPref);
  const keysW = kit ? KIT_KEYS_W : KEYS_W;
  const [octave, setOctave] = useState(3);
  const [cursor, setCursor] = useState(0);
  const [take, setTake] = useState<WireNote[]>([]);
  const takeRef = useRef<WireNote[]>([]);
  takeRef.current = take;
  const held = useRef(new Map<string, { pitch: number; downBeat: number; velocity: number; wrapped?: boolean; release?: () => void }>());
  // A launched clip's playhead wraps; a key still down when it does was held
  // across the clip's end (once or many times) and is one note to the end.
  const lastPlayhead = useRef<number | undefined>(undefined);
  useEffect(() => {
    const prev = lastPlayhead.current;
    lastPlayhead.current = playheadBeats;
    if (prev === undefined || playheadBeats === undefined || playheadBeats >= prev - 0.5) return;
    for (const entry of held.current.values()) entry.wrapped = true;
  }, [playheadBeats]);
  const flushTimer = useRef<number | null>(null);
  const notes = useMemo(() => (take.length === 0 ? committed : [...committed, ...take]), [committed, take]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drag, setDrag] = useState<Drag | null>(null);
  const [laneKind, setLaneKind] = usePref<LaneKind>("seedbed-viz.rollLane", "velocity", isLaneKind);
  const [velDrag, setVelDrag] = useState<{ keys: Set<string>; kind: LaneKind; value: number } | null>(null);
  /** Horizontal zoom, ×fit. The three horizontal strips (ruler, grid,
   * velocity) are each `zoom × 100%` wide inside their own scrollers, kept
   * in step by mirroring scrollLeft. */
  const [zoom, setZoom] = useState(1);
  /** The roll's own grid, kept per browser. */
  const [GRID, setGridState] = useState(() => {
    try {
      const v = Number(localStorage.getItem("seedbed-viz.rollGrid"));
      return GRID_CHOICES.some((g) => Math.abs(g.value - v) < 1e-9) ? v : DEFAULT_GRID;
    } catch {
      return DEFAULT_GRID;
    }
  });
  const setGrid = (v: number) => {
    setGridState(v);
    setNoteLength(v);
    try {
      localStorage.setItem("seedbed-viz.rollGrid", String(v));
    } catch {
      // a private window
    }
  };
  // A scale to draw against — a per-browser aid, never on the wire.
  const [scaleChoice, setScaleChoiceState] = useState<{ root: number; scaleId: string }>(() => {
    try {
      const raw = localStorage.getItem("seedbed-viz.scale");
      const parsed = raw ? (JSON.parse(raw) as { root: number; scaleId: string }) : null;
      return parsed && Number.isInteger(parsed.root) ? parsed : { root: 0, scaleId: "" };
    } catch {
      return { root: 0, scaleId: "" };
    }
  });
  const setScaleChoice = (next: { root: number; scaleId: string }) => {
    setScaleChoiceState(next);
    try {
      localStorage.setItem("seedbed-viz.scale", JSON.stringify(next));
    } catch {
      // a private window
    }
  };
  // The Set's scale wins over the browser's pick while Live's Scale Mode is
  // on (off, Live highlights nothing either — the browser's own aid stays):
  // `scale` and `scaleRoot` are what every tint and fold below read.
  // A kit has no scale: its rows are drums, not degrees, so neither the Set's
  // key nor the browser's aid tints, folds or hides anything on a kit lane.
  const setScale = !kit && roomScale?.mode ? roomScale : undefined;
  const localScale = kit ? null : scaleById(scaleChoice.scaleId);
  const scale = setScale ? { id: "set", label: setScale.name, intervals: setScale.intervals } : localScale;
  const scaleRoot = setScale ? setScale.root : scaleChoice.root;
  const outOfScale = (pitch: number) => scale !== null && !inScale(pitch, scaleRoot, scale.intervals);
  const stripRefs = useRef<Array<HTMLDivElement | null>>([null, null, null]);
  const syncScroll = (from: number) => (e: React.UIEvent<HTMLDivElement>) => {
    const left = e.currentTarget.scrollLeft;
    stripRefs.current.forEach((el, i) => {
      if (i !== from && el && el.scrollLeft !== left) el.scrollLeft = left;
    });
  };
  /** A rubber band on the grid, in beats × pitches, while being drawn. */
  const [band, setBand] = useState<{ b0: number; b1: number; p0: number; p1: number } | null>(null);
  const [hovered, setHovered] = useState<WireNote | null>(null);
  // A drag ends with a click event; without this the drop would also select.
  const suppressClick = useRef(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const editable = onChange !== undefined;
  const span = Math.max(clip.duration, 1);
  const fill = clipColor(clip.color);
  const pct = (b: number) => `${(b / span) * 100}%`;
  const snap = (b: number) => Math.round(b / GRID) * GRID;

  // The wire said the clip HAS notes it did not carry (over the per-clip cap).
  // Drawing the empty grid would invite an edit that erases them all.
  const omitted = clip.notes === undefined && (clip.noteCount ?? 0) > 0;

  // A clip opened is a fresh start: nothing selected, nothing mid-drag.
  useEffect(() => {
    setSelected(new Set());
    setDrag(null);
  }, [clip.id]);

  // Land the scroll on the clip's content, once per clip opened — not on every
  // note change, which would yank the view around mid-edit — and again when
  // the roll takes over from the step view (a kit opens on the grid, so the
  // roll's scroll box did not exist to land on yet).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const y = (127 - centerPitch(clip.notes ?? [])) * ROW_H - el.clientHeight / 2;
    el.scrollTop = Math.max(0, y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip.id, steps]);

  // Ruler labels: ~8 over the span, on a musically sane spacing — numbered
  // as the timeline numbers them, in bars from 1 (bar.beat between bars).
  const step = useMemo(() => {
    for (const candidate of [1, 2, beatsPerBar, beatsPerBar * 2, beatsPerBar * 4, beatsPerBar * 8, beatsPerBar * 16, beatsPerBar * 32]) {
      if (span / candidate <= 8 * zoom) return candidate;
    }
    return beatsPerBar * 64;
  }, [span, beatsPerBar, zoom]);
  const barLabel = (b: number): string => {
    const bar = Math.floor(b / beatsPerBar) + 1;
    const beat = Math.round(b - (bar - 1) * beatsPerBar) + 1;
    return beat === 1 ? String(bar) : `${bar}.${beat}`;
  };

  /** What is drawn right now — the props' notes with any live drag folded in. */
  const shown = useMemo(() => {
    if (drag && drag.moved) {
      if (drag.copy && drag.mode === "move") return copyDragNotes(notes, drag.keys, drag.deltaBeats, drag.deltaPitch, clip.duration).notes;
      return dragNotes(notes, drag.keys, drag.mode, drag.deltaBeats, drag.deltaPitch, clip.duration);
    }
    if (velDrag) return applyLaneValue(notes, velDrag.keys, velDrag.kind, velDrag.value);
    if (erasing && erasing.size > 0) return notes.filter((n) => !erasing.has(noteKey(n)));
    return notes;
  }, [notes, drag, velDrag, erasing, clip.duration]);

  const commit = (next: WireNote[]) => onChange?.(next);

  function beginNoteDrag(e: React.PointerEvent<HTMLButtonElement>, note: WireNote) {
    if (!editable) return;
    e.stopPropagation();
    // The right button erases (FL's gesture): this note, and every note the
    // pointer crosses before it lifts.
    if (e.button === 2) {
      beginErase(e, note);
      return;
    }
    if (e.button !== 0) return;
    const box = e.currentTarget.getBoundingClientRect();
    const grid = gridRef.current?.getBoundingClientRect();
    if (!grid) return;
    const mode: Drag["mode"] =
      box.width > EDGE_PX * 2 && e.clientX > box.right - EDGE_PX ? "end" : "move";
    const key = noteKey(note);
    const keys =
      mode === "move" && selected.has(key) && selected.size > 1 ? new Set(selected) : new Set([key]);
    // ⌥ (Live) or ⇧ (FL) at the press: a clone drag — the originals stay.
    const copy = mode === "move" && (e.altKey || e.shiftKey);
    const state: Drag = { mode, copy, keys, deltaBeats: 0, deltaPitch: 0, moved: false };
    setDrag(state);

    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - e.clientX;
      const dy = ev.clientY - e.clientY;
      state.moved ||= Math.abs(dx) > DRAG_SLOP_PX || Math.abs(dy) > DRAG_SLOP_PX;
      // ⌘ / Ctrl held while dragging: snap off (FL's Alt, Live's ⌘) — the
      // note lands where the hand leaves it, to a ten-thousandth of a beat.
      const raw = (dx / grid.width) * span;
      state.deltaBeats = ev.metaKey || ev.ctrlKey ? Math.round(raw * 1e4) / 1e4 : snap(raw);
      state.deltaPitch = mode === "move" ? -Math.round(dy / ROW_H) : 0;
      setDrag({ ...state });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setDrag(null);
      if (!state.moved) return; // a click that wobbled — leave it to onClick
      suppressClick.current = true;
      const { notes: next, placed } = state.copy
        ? copyDragNotes(notes, state.keys, state.deltaBeats, state.deltaPitch, clip.duration)
        : dragNotesPlaced(notes, state.keys, state.mode, state.deltaBeats, state.deltaPitch, clip.duration);
      if (placed.length === 0) return; // a clone drag that went nowhere
      // Selection keys are note values; the moved notes have new ones, and
      // a same-pitch neighbour they overwrote is gone — reselect by what
      // was placed, not by position.
      setSelected(new Set(placed.map(noteKey)));
      // A resize teaches the next note its length (FL's memory).
      if (state.mode === "end" && placed.length === 1) setNoteLength(placed[0]!.duration);
      commit(next);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  /**
   * A right-button sweep (FL's eraser): the pressed note goes, and so does
   * every note the pointer crosses before the button lifts — one edit on
   * release. From empty grid the sweep starts with nothing and erases what
   * it reaches. The context menu stays shut.
   */
  function beginErase(e: React.PointerEvent<HTMLElement>, first: WireNote | null) {
    if (!editable || eraseRef.current) return;
    e.preventDefault();
    const state = new Set<string>(first ? [noteKey(first)] : []);
    eraseRef.current = state;
    setErasing(new Set(state));
    const pointerId = e.pointerId;
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const el = document.elementFromPoint(ev.clientX, ev.clientY)?.closest<HTMLElement>("[data-note-key]");
      const key = el?.dataset["noteKey"];
      if (!key || state.has(key)) return;
      state.add(key);
      setErasing(new Set(state));
    };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      eraseRef.current = null;
      setErasing(null);
      suppressClick.current = true;
      if (state.size === 0) return;
      commit(deleteNotes(notes, state));
      setSelected((prev) => {
        const next = new Set(prev);
        for (const k of state) next.delete(k);
        return next;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  function beginVelocityDrag(e: React.PointerEvent<HTMLButtonElement>, note: WireNote) {
    if (!editable) return;
    e.stopPropagation();
    const lane = e.currentTarget.parentElement?.getBoundingClientRect();
    if (!lane) return;
    const key = noteKey(note);
    const kind = laneKind;
    // A drag on a selected bar sets the whole selection; on any other, that note.
    const keys = selected.has(key) ? new Set(selected) : new Set([key]);
    const at = (clientY: number): number => {
      const up = (lane.bottom - clientY) / VEL_H; // 0 at the bottom, 1 at the top
      if (kind === "velocity") return Math.min(Math.max(1, Math.round(up * 127)), 127);
      if (kind === "chance") return Math.min(Math.max(0, Math.round(up * 100) / 100), 1);
      return Math.min(Math.max(-127, Math.round((up - 0.5) * 2 * 127)), 127);
    };
    const start = laneValue(note, kind);
    let value = start;
    setVelDrag({ keys, kind, value });
    const move = (ev: PointerEvent) => {
      value = at(ev.clientY);
      setVelDrag({ keys, kind, value });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setVelDrag(null);
      if (value !== start) {
        commit(kind === "velocity" && keys.size === 1 ? setNoteVelocity(notes, key, value) : applyLaneValue(notes, keys, kind, value));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  /** Double-click on empty grid: draw a note there. */
  function drawAt(e: React.MouseEvent<HTMLDivElement>) {
    if (!editable) return;
    const grid = gridRef.current?.getBoundingClientRect();
    if (!grid) return;
    const pitch = 127 - Math.floor((e.clientY - grid.top) / ROW_H);
    const beat = ((e.clientX - grid.left) / grid.width) * span;
    // The remembered length, and the stamp's chord if one is set.
    const start = Math.floor(beat / GRID) * GRID;
    const { notes: next, added } = stampChord(notes, pitch, chordId, start, noteLength, clip.duration);
    if (added.length === 0) return;
    setSelected(new Set(added.map(noteKey)));
    for (const a of added) onAudition?.(a);
    commit(next);
  }

  function removeSelection() {
    if (!editable || selected.size === 0) return;
    commit(deleteNotes(notes, selected));
    setSelected(new Set());
  }

  /** Arrow keys: transpose the selection (shift = an octave). Selection keys
   * are note values, so reselect the moved notes positionally. */
  function transpose(semitones: number) {
    if (!editable || selected.size === 0) return;
    const next = transposeNotes(notes, selected, semitones, clip.duration);
    setSelected(
      new Set(next.filter((_, i) => notes[i] !== undefined && selected.has(noteKey(notes[i]!))).map(noteKey)),
    );
    commit(next);
    // Sound the lowest moved note as feedback for the new pitch.
    const moved = next.filter((_, i) => notes[i] !== undefined && selected.has(noteKey(notes[i]!)));
    const lowest = moved.reduce<WireNote | null>((m, n) => (m === null || n.pitch < m.pitch ? n : m), null);
    if (lowest) onAudition?.(lowest);
  }

  /** Pointer down on empty grid: draw a rubber band over notes. A click that
   * never moves clears the selection, as before. */
  /**
   * Draw mode: press on empty grid → a note at the grid, then drag right to
   * set its length. The note rides in the take (like typed notes) and is
   * proposed once, on release, so drawing costs one intent, not two.
   */
  function beginDraw(e: React.PointerEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || !editable) return;
    if (e.button === 2) {
      beginErase(e, null);
      return;
    }
    if (e.button !== 0) return;
    const grid = gridRef.current?.getBoundingClientRect();
    if (!grid) return;
    const pitch = Math.min(127, Math.max(0, 127 - Math.floor((e.clientY - grid.top) / ROW_H)));
    const beatAt = (clientX: number) => ((clientX - grid.left) / grid.width) * span;
    const start = Math.min(Math.max(0, Math.floor(beatAt(e.clientX) / GRID) * GRID), Math.max(0, clip.duration - MIN_NOTE_BEATS));
    // The remembered length (FL), the stamp's chord if one is set; a drag
    // right then sets the length of everything drawn.
    const drawn = stampChord([], pitch, chordId, start, noteLength, clip.duration).added;
    if (drawn.length === 0) return;
    const count = drawn.length;
    takeRef.current = [...takeRef.current, ...drawn];
    setTake(takeRef.current);
    for (const d of drawn) onAudition?.(d);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    let dragged = false;
    const move = (ev: PointerEvent) => {
      const end = Math.ceil(beatAt(ev.clientX) / GRID) * GRID;
      const duration = Math.max(MIN_NOTE_BEATS, Math.min(end - start, clip.duration - start));
      const tail = takeRef.current.slice(-count);
      if (tail.length === 0 || tail[0]!.duration === duration) return;
      dragged = true;
      takeRef.current = [...takeRef.current.slice(0, -count), ...tail.map((t) => ({ ...t, duration }))];
      setTake(takeRef.current);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      suppressClick.current = true;
      const tail = takeRef.current.slice(-count);
      if (tail.length > 0) setSelected(new Set(tail.map(noteKey)));
      if (dragged && tail[0]) setNoteLength(tail[0].duration);
      flushTake();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  /**
   * Paint mode (FL's brush): a press lays a note of the remembered length at
   * the grid cell under it, and the sweep lays one at every cell it crosses
   * — any pitch, any direction — never stacking a cell. The stamp's chord
   * paints as chords. Everything painted rides in the take and is proposed
   * once, on release.
   */
  function beginPaint(e: React.PointerEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || !editable) return;
    if (e.button === 2) {
      beginErase(e, null);
      return;
    }
    if (e.button !== 0) return;
    const grid = gridRef.current?.getBoundingClientRect();
    if (!grid) return;
    const cellAt = (clientX: number, clientY: number) => ({
      pitch: Math.min(127, Math.max(0, 127 - Math.floor((clientY - grid.top) / ROW_H))),
      start: Math.floor((((clientX - grid.left) / grid.width) * span) / GRID) * GRID,
    });
    const intervals = CHORDS.find((c) => c.id === chordId)?.intervals ?? [0];
    const paintAt = (clientX: number, clientY: number) => {
      const { pitch, start } = cellAt(clientX, clientY);
      let all: WireNote[] = [...committed, ...takeRef.current];
      const fresh: WireNote[] = [];
      for (const i of intervals) {
        const p = pitch + i;
        if (p < 0 || p > 127) continue;
        const next = paintNote(all, p, start, noteLength, clip.duration);
        if (next.length > all.length) fresh.push(next[next.length - 1]!);
        all = next;
      }
      if (fresh.length === 0) return;
      takeRef.current = [...takeRef.current, ...fresh];
      setTake(takeRef.current);
      for (const f of fresh) onAudition?.(f);
    };
    paintAt(e.clientX, e.clientY);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pointerId = e.pointerId;
    const move = (ev: PointerEvent) => {
      if (ev.pointerId === pointerId) paintAt(ev.clientX, ev.clientY);
    };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      suppressClick.current = true;
      flushTake();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  function beginBand(e: React.PointerEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    if (e.button === 2) {
      beginErase(e, null);
      return;
    }
    if (e.button !== 0) return;
    const grid = gridRef.current?.getBoundingClientRect();
    if (!grid) return;
    const at = (clientX: number, clientY: number) => ({
      beat: ((clientX - grid.left) / grid.width) * span,
      pitch: 127 - Math.floor((clientY - grid.top) / ROW_H),
    });
    const origin = at(e.clientX, e.clientY);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    let moved = false;
    const state = { b0: origin.beat, b1: origin.beat, p0: origin.pitch, p1: origin.pitch };
    const move = (ev: PointerEvent) => {
      moved ||= Math.abs(ev.clientX - e.clientX) > DRAG_SLOP_PX || Math.abs(ev.clientY - e.clientY) > DRAG_SLOP_PX;
      if (!moved) return;
      const here = at(ev.clientX, ev.clientY);
      state.b1 = here.beat;
      state.p1 = here.pitch;
      setBand({ ...state });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setBand(null);
      if (!moved) return; // the click handler clears
      suppressClick.current = true;
      const hit = notesWithin(notes, state.b0, state.b1, state.p0, state.p1);
      setSelected((prev) => (additive ? new Set([...prev, ...hit]) : hit));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }

  function muteSelection() {
    if (!editable || selected.size === 0) return;
    commit(toggleNotesMuted(notes, selected));
    // Keys are pitch:start:duration — mute doesn't change them.
  }

  function duplicateSelection() {
    if (!editable || selected.size === 0) return;
    const { notes: next, added } = duplicateNotes(notes, selected, GRID, clip.duration);
    if (added.length === 0) return;
    setSelected(new Set(added.map(noteKey)));
    commit(next);
  }

  /** ← → : the selection slides by `deltaBeats` (clamped as a group). */
  function nudge(deltaBeats: number) {
    if (!editable || selected.size === 0) return;
    const { notes: next, placed } = dragNotesPlaced(notes, selected, "move", deltaBeats, 0, clip.duration);
    if (placed.length === 0 || next.every((n, i) => n === notes[i])) return;
    setSelected(new Set(placed.map(noteKey)));
    commit(next);
  }

  /** ⌘C / ⌘X: the selection onto the page's note clipboard (cut removes it). */
  function copySelection(cut: boolean) {
    if (selected.size === 0) return;
    noteClipboard = notes.filter((n) => selected.has(noteKey(n)));
    if (cut && editable) removeSelection();
  }

  /**
   * ⌘V: the clipboard lands where the playhead runs through this clip, or
   * — as FL pastes — at the first grid line of the bars scrolled into view.
   */
  function pasteClipboard() {
    if (!editable || noteClipboard.length === 0) return;
    let at = 0;
    if (playheadBeats !== undefined) at = Math.round(playheadBeats / GRID) * GRID;
    else {
      const strip = stripRefs.current[1];
      if (strip && strip.scrollWidth > 0) at = Math.ceil((strip.scrollLeft / strip.scrollWidth) * span / GRID - 1e-6) * GRID;
    }
    const { notes: next, added } = pasteNotes(notes, noteClipboard, Math.max(0, at), clip.duration);
    if (added.length === 0) return;
    setSelected(new Set(added.map(noteKey)));
    commit(next);
  }

  /** ⇧-click on a key: every note on that pitch (⌘ adds them to the selection). */
  function selectPitch(pitch: number, additive: boolean) {
    const hit = notesAtPitch(notes, pitch);
    setSelected((prev) => (additive ? new Set([...prev, ...hit]) : hit));
  }

  /** Reselect positionally after a transform that changes note values. */
  function reselect(next: WireNote[]) {
    setSelected(
      new Set(next.filter((_, i) => notes[i] !== undefined && selected.has(noteKey(notes[i]!))).map(noteKey)),
    );
  }

  function legato() {
    if (!editable) return;
    const next = legatoNotes(notes, selected);
    if (next.every((n, i) => n === notes[i])) return;
    reselect(next);
    commit(next);
  }

  /** Ramp the selection's velocities from its earliest note's to its latest's. */
  function ramp() {
    if (!editable || selected.size < 2) return;
    const picked = notes.filter((n) => selected.has(noteKey(n))).sort((a, b) => a.startTime - b.startTime);
    const from = picked[0]!.velocity;
    const to = picked[picked.length - 1]!.velocity;
    const next = rampVelocity(notes, selected, from, to);
    if (next.every((n, i) => n === notes[i])) return;
    reselect(next);
    commit(next);
  }

  function fold() {
    if (!editable || !scale) return;
    const next = foldToScale(notes, selected, scaleRoot, scale.intervals);
    if (next.every((n, i) => n === notes[i])) return;
    reselect(next);
    commit(next);
  }

  /** One shared path for the whole-list transforms: skip no-ops, reselect
   * positionally when the transform kept the list's order (a map), else
   * clear — chop and arp add, drop and reorder, so positions mean nothing. */
  function transform(next: WireNote[], keepsOrder: boolean) {
    if (!editable) return;
    if (next.length === notes.length && next.every((n, i) => n === notes[i])) return;
    if (keepsOrder && next.length === notes.length) reselect(next);
    else setSelected(new Set());
    commit(next);
  }

  function humanize() {
    if (!editable) return;
    const next = humanizeNotes(notes, selected, HUMANIZE_BEATS, HUMANIZE_VELOCITY, clip.duration);
    reselect(next);
    commit(next);
  }

  // Groove: how far a quantize moves a note, and how late the off-beat sits
  // — this browser's settings, remembered.
  const [groove, setGroove] = usePref<Groove>("seedbed-viz.groove", DEFAULT_GROOVE, isGroove);
  function quantize(grid: number) {
    if (!editable) return;
    const next =
      groove.strength >= 1 && groove.swing <= 0
        ? quantizeNotes(notes, selected, grid, clip.duration)
        : grooveNotes(notes, selected, grid, groove, clip.duration);
    if (next.every((n, i) => n === notes[i])) return;
    setSelected(
      new Set(next.filter((_, i) => notes[i] !== undefined && selected.has(noteKey(notes[i]!))).map(noteKey)),
    );
    commit(next);
  }

  const flushTake = useCallback(() => {
    if (flushTimer.current !== null) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    // Read through a ref and propose OUTSIDE the updater: StrictMode runs
    // updaters twice in development, and a proposal is not idempotent.
    const pending = takeRef.current;
    if (pending.length === 0) return;
    takeRef.current = [];
    setTake([]);
    onChange?.([...committed, ...pending]);
  }, [committed, onChange]);
  const scheduleFlush = useCallback(() => {
    if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
    flushTimer.current = window.setTimeout(flushTake, TYPE_FLUSH_MS);
  }, [flushTake]);
  // A take never outlives the clip it was typed into, or the keyboard mode.
  useEffect(() => () => {
    if (flushTimer.current !== null) window.clearTimeout(flushTimer.current);
  }, []);
  useEffect(() => {
    if (!kbd) {
      flushTake();
      held.current.clear();
    }
  }, [kbd, flushTake]);

  /** A note key went down: sound it; record it live against a running
   * transport, or step-insert it at the cursor. */
  function keyDown(e: React.KeyboardEvent): boolean {
    if (!kbd || !editable || e.metaKey || e.ctrlKey || e.altKey) return false;
    const key = e.key.toLowerCase();
    if (key === "z" || key === "x") {
      setOctave((o) => Math.max(OCTAVE_MIN, Math.min(OCTAVE_MAX, o + (key === "z" ? -1 : 1))));
      return true;
    }
    if ((key === "arrowleft" || key === "arrowright") && !(e.shiftKey && selected.size > 0)) {
      setCursor((c) => Math.max(0, Math.min(clip.duration, c + (key === "arrowleft" ? -GRID : GRID))));
      return true;
    }
    if ((key === "backspace" || key === "delete") && playheadBeats === undefined && selected.size === 0) {
      // Step back one grid and drop whatever was stepped in there.
      const at = Math.max(0, cursor - GRID);
      setCursor(at);
      setTake((t) => t.filter((n) => Math.abs(n.startTime - at) > 1e-9));
      const remaining = committed.filter((n) => Math.abs(n.startTime - at) > 1e-9);
      if (remaining.length !== committed.length) onChange?.(remaining);
      return true;
    }
    const pitch = pitchForKey(key, octave);
    if (pitch === null) return false;
    if (e.repeat) return true;
    noteOn(key, pitch, 100);
    return true;
  }

  /** A note key came up: in live mode the note's length is how long it was
   * held; in step mode the cursor advances once the chord's last key lifts. */
  function keyUp(e: React.KeyboardEvent): void {
    if (!kbd) return;
    noteOff(e.key.toLowerCase());
  }

  /** Note on, from either keyboard. `id` pairs it with its note-off. */
  const noteOn = useCallback(
    (id: string, pitch: number, velocity: number) => {
      // The key sounds for as long as it is held: `onAudition` hands back
      // the note's release, called at key up.
      if (playheadBeats !== undefined) {
        const release = onAudition?.({ pitch, startTime: 0, duration: 4, velocity }) ?? undefined;
        held.current.set(id, { pitch, downBeat: playheadBeats, velocity, release });
        return;
      }
      const result = stepInsert(notes, pitch, cursor, GRID, clip.duration, velocity);
      const release = onAudition?.(result.note) ?? undefined;
      setTake((t) => [...t, result.note]);
      held.current.set(id, { pitch, downBeat: cursor, velocity, release });
      scheduleFlush();
    },
    [playheadBeats, notes, cursor, clip.duration, onAudition, scheduleFlush],
  );
  const noteOff = useCallback(
    (id: string) => {
      const down = held.current.get(id);
      if (!down) return;
      held.current.delete(id);
      down.release?.();
      if (playheadBeats !== undefined) {
        const note = playedNote(down.pitch, down.downBeat, down.wrapped ? clip.duration : playheadBeats, GRID, clip.duration, down.velocity, looping);
        if (note) {
          setTake((t) => [...t, note]);
          scheduleFlush();
        }
        return;
      }
      if (held.current.size === 0) {
        setCursor(stepInsert(notes, down.pitch, down.downBeat, GRID, clip.duration).nextCursor);
      }
    },
    [playheadBeats, looping, notes, clip.duration, scheduleFlush],
  );

  // A real MIDI keyboard, while "keys" is on: note on/off land in the same
  // take as typed notes, velocity kept. Web MIDI is Chromium-only and asks
  // permission once; browsers without it simply never deliver messages.
  const midiRef = useRef({ noteOn, noteOff });
  midiRef.current = { noteOn, noteOff };
  const [midiInputs, setMidiInputs] = useState<number | null>(null);
  useEffect(() => {
    if (!kbd || !editable) return;
    if (typeof navigator.requestMIDIAccess !== "function") {
      setMidiInputs(null);
      return;
    }
    let access: MIDIAccess | null = null;
    let cancelled = false;
    const onMessage = (e: MIDIMessageEvent) => {
      const data = e.data;
      if (!data || data.length < 3) return;
      const status = data[0]! & 0xf0;
      const pitch = data[1]!;
      const velocity = data[2]!;
      if (status === 0x90 && velocity > 0) midiRef.current.noteOn(`midi:${pitch}`, pitch, velocity);
      else if (status === 0x80 || (status === 0x90 && velocity === 0)) midiRef.current.noteOff(`midi:${pitch}`);
    };
    const attach = () => {
      if (!access) return;
      let n = 0;
      access.inputs.forEach((input) => {
        input.onmidimessage = onMessage;
        n++;
      });
      setMidiInputs(n);
    };
    navigator
      .requestMIDIAccess()
      .then((a) => {
        if (cancelled) return;
        access = a;
        attach();
        a.onstatechange = attach;
      })
      .catch(() => setMidiInputs(null));
    return () => {
      cancelled = true;
      if (access) {
        access.onstatechange = null;
        access.inputs.forEach((input) => {
          input.onmidimessage = null;
        });
      }
    };
  }, [kbd, editable]);

  const rows = Array.from({ length: 128 }, (_, i) => 127 - i);
  /** What a key reads: its drum on a kit, else its pitch at each C. */
  const keyLabel = (pitch: number): string => (kit ? drumLabel(pitch, drumNames) : null) ?? (pitch % 12 === 0 ? pitchName(pitch) : "");
  const keyTitle = (pitch: number): string => {
    const drum = kit ? drumLabel(pitch, drumNames) : null;
    return drum ? `${drum} · ${pitchName(pitch)}` : pitchName(pitch);
  };

  const toolbar = (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-border px-3 py-2">
        <span className="size-2.5 rounded-sm" style={{ backgroundColor: fill }} />
        <span className="truncate text-xs font-medium">♪ {clip.name || "(unnamed)"}</span>
        <span className="text-[11px] text-muted-foreground tabular">
          {shown.length} note{shown.length === 1 ? "" : "s"} · {beats(clip.duration)} beats
        </span>
        {pending && <Badge variant="outline">syncing…</Badge>}
        {!editable && <Badge variant="outline">read-only</Badge>}
        {editable && onScaleLoop && (
          <span className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
            <button
              type="button"
              onClick={() => onScaleLoop(2)}
              title="×2 — double the clip, its notes repeating in the new half (refused if it would overlap the next clip)"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              ×2
            </button>
            <button
              type="button"
              onClick={() => onScaleLoop(0.5)}
              title="÷2 — halve the clip, keeping the notes in its first half"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              ÷2
            </button>
          </span>
        )}
        {editable && !omitted && (
          <label className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground" title="Grid — what new notes snap to and drags move by">
            <span>grid</span>
            <select
              value={String(GRID)}
              onChange={(e) => setGrid(Number(e.target.value))}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-6 rounded-md border border-input bg-transparent px-1 text-[11px]"
              aria-label="Note grid"
            >
              {GRID_CHOICES.map((g) => (
                <option key={g.label} value={String(g.value)}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {editable && !omitted && (
          <label className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground" title="Quantize note starts — the selection, or every note">
            <span>quantize</span>
            <select
              value=""
              onChange={(e) => {
                const g = Number(e.target.value);
                if (g > 0) quantize(g);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-6 rounded-md border border-input bg-transparent px-1 text-[11px]"
              aria-label="Quantize"
            >
              <option value="">{selected.size > 0 ? `${selected.size} selected` : "all notes"}</option>
              <option value="0.25">1/16</option>
              <option value="0.5">1/8</option>
              <option value="1">1/4</option>
              <option value="0.125">1/32</option>
            </select>
            <PercentField
              label="str"
              title="Quantize strength — how far each note moves toward the grid (100% snaps it)"
              value={groove.strength}
              onCommit={(v) => setGroove({ ...groove, strength: v })}
            />
            <PercentField
              label="swing"
              title="Swing — how late every second grid step sits, as a fraction of the grid (50% at 1/8 is the 2:1 shuffle)"
              value={groove.swing}
              onCommit={(v) => setGroove({ ...groove, swing: v })}
            />
            <button
              type="button"
              onClick={legato}
              title="Legato — stretch each note (the selection, or all) to the next note's start"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              legato
            </button>
            <button
              type="button"
              onClick={ramp}
              disabled={selected.size < 2}
              title="Velocity ramp — from the earliest selected note's velocity to the latest's, by time"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground disabled:opacity-40"
            >
              ramp
            </button>
            <button
              type="button"
              onClick={() => transform(chopNotes(notes, selected, GRID), false)}
              title="Chop — cut each note (the selection, or all) into grid-length repeats"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              chop
            </button>
            <button
              type="button"
              onClick={() => transform(arpeggiateNotes(notes, selected, GRID), false)}
              title="Arpeggiate — chords in the selection become runs up through their pitches at the grid rate"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              arp
            </button>
            <button
              type="button"
              onClick={() => transform(reverseNotes(notes, selected), true)}
              title="Reverse — mirror the selection (or all) in time within its own span"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              reverse
            </button>
            <button
              type="button"
              onClick={humanize}
              title="Humanize — nudge timing by up to a 64th and velocity by up to ±10 (the selection, or all)"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              humanize
            </button>
            <button
              type="button"
              onClick={() => transform(glueNotes(notes, selected), false)}
              title="Glue (⌘G) — join touching notes on a pitch into one (the selection, or all)"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              glue
            </button>
            <button
              type="button"
              onClick={() => transform(flipNotes(notes, selected), true)}
              title="Flip — mirror pitches within their range, the top note to the bottom"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              flip
            </button>
            <button
              type="button"
              onClick={() => transform(strumNotes(notes, selected, STRUM_BEATS), true)}
              title="Strum — spread each chord's notes a 64th apart, up from the bottom, ends fixed"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              strum
            </button>
            <button
              type="button"
              onClick={() => transform(flamNotes(notes, selected, FLAM_BEATS), false)}
              title="Flam — a quieter grace note a 64th before each note"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              flam
            </button>
            <button
              type="button"
              onClick={() => transform(scaleVelocity(notes, selected, 0.8), true)}
              title="Softer — velocities × 0.8, dynamics kept"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              softer
            </button>
            <button
              type="button"
              onClick={() => transform(scaleVelocity(notes, selected, 1.25), true)}
              title="Louder — velocities × 1.25, dynamics kept"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              louder
            </button>
            <button
              type="button"
              onClick={() => transform(scaleLength(notes, selected, 0.5, clip.duration), false)}
              title="Staccato — note lengths halved, starts kept"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              ½ len
            </button>
            <button
              type="button"
              onClick={() => transform(scaleLength(notes, selected, 2, clip.duration), false)}
              title="Stretch — note lengths doubled, cut at the clip; a stretched note overwrites what it reaches"
              className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
            >
              ×2 len
            </button>
          </label>
        )}
        {!omitted && !kit && (
          <span className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground" title={setScale ? "The Set's scale, as Live reports it — tints the rows outside it; Fold snaps notes onto it. Change it in Live." : "Scale — tints the rows outside it; Fold snaps notes onto it. A drawing aid in this browser, not the Set's key."}>
            {roomScale && (
              <span className="rounded-md border border-border px-1.5 py-0.5" data-testid="room-scale" title={roomScale.mode ? undefined : "Live's Scale Mode is off — the browser's own scale aid applies"}>
                Set: {ROOTS[roomScale.root]} {roomScale.name}
                {roomScale.mode ? "" : " (off)"}
              </span>
            )}
            {!setScale && (
            <select
              value={scaleChoice.root}
              onChange={(e) => setScaleChoice({ ...scaleChoice, root: Number(e.target.value) })}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-6 rounded-md border border-input bg-transparent px-1 text-[11px]"
              aria-label="Scale root"
            >
              {ROOTS.map((r, i) => (
                <option key={r} value={i}>
                  {r}
                </option>
              ))}
            </select>
            )}
            {!setScale && (
            <select
              value={scaleChoice.scaleId}
              onChange={(e) => setScaleChoice({ ...scaleChoice, scaleId: e.target.value })}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-6 rounded-md border border-input bg-transparent px-1 text-[11px]"
              aria-label="Scale"
            >
              <option value="">no scale</option>
              {SCALES.map((sc) => (
                <option key={sc.id} value={sc.id}>
                  {sc.label}
                </option>
              ))}
            </select>
            )}
            {editable && scale && (
              <button
                type="button"
                onClick={fold}
                title={`Fold the selection (or every note) onto ${ROOTS[scaleRoot]} ${scale.label}`}
                className="rounded-md border border-border px-1.5 py-0.5 hover:text-foreground"
              >
                fold
              </button>
            )}
          </span>
        )}
        {editable && !omitted && (
          <span className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
            <button
              type="button"
              onClick={() => {
                const next = !steps;
                setStepsOverride(next);
                if (!kit) setStepsPref(next);
              }}
              aria-pressed={steps}
              title="Step view — a drum machine over the clip: tap a cell for a hit, tap again to clear it, drag to paint a run, drag a hit to move it, ⇧-tap to accent / ghost"
              className={cn(
                "flex items-center gap-1 rounded-md border px-1.5 py-0.5",
                steps ? "border-[var(--primary)] text-primary" : "border-border hover:text-foreground",
              )}
            >
              <Grid3x3 className="size-3" /> steps
            </button>
            <button
              type="button"
              onClick={() => setDrawMode((d) => !d)}
              aria-pressed={drawMode}
              title="Draw mode (B) — press on empty grid to draw a note, drag right to set its length"
              className={cn(
                "flex items-center gap-1 rounded-md border px-1.5 py-0.5",
                drawMode ? "border-[var(--primary)] text-primary" : "border-border hover:text-foreground",
              )}
            >
              <Pencil className="size-3" /> draw
            </button>
            <button
              type="button"
              onClick={() => setPaintMode((d) => !d)}
              aria-pressed={paintMode}
              title="Paint mode (P) — sweep across the grid to lay a note in every cell you cross, each as long as the last note drawn"
              className={cn(
                "flex items-center gap-1 rounded-md border px-1.5 py-0.5",
                paintMode ? "border-[var(--primary)] text-primary" : "border-border hover:text-foreground",
              )}
            >
              <Paintbrush className="size-3" /> paint
            </button>
            {!steps && (
              <select
                value={chordId}
                onChange={(e) => setChordId(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                title="Chord stamp — what a draw places on the pressed note"
                aria-label="Chord stamp"
                className="h-6 rounded-md border border-input bg-transparent px-1 text-[11px]"
              >
                <option value="">single note</option>
                {CHORDS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            )}
            {ghostNotes && !steps && (
              <button
                type="button"
                onClick={() => setGhosts(!ghosts)}
                aria-pressed={ghosts}
                title="Ghost notes — the other MIDI lanes' notes under this clip, dimmed and untouchable"
                className={cn(
                  "flex items-center gap-1 rounded-md border px-1.5 py-0.5",
                  ghosts ? "border-[var(--primary)] text-primary" : "border-border hover:text-foreground",
                )}
              >
                <Ghost className="size-3" /> ghosts
              </button>
            )}
            <button
              type="button"
              onClick={() => setKbd((k) => !k)}
              aria-pressed={kbd}
              title={
                kbd
                  ? "Keyboard input on — A W S E D F T G Y H U J K play notes, Z/X shift the octave. Transport stopped: notes step in at the cursor (←/→ move it, Backspace steps back). Transport running through this clip, or the clip launched from its slot: notes record where the playhead is (overdub)."
                  : "Play notes from the computer keyboard (step input, or live against the transport)"
              }
              className={cn(
                "flex items-center gap-1 rounded-md border px-1.5 py-0.5",
                kbd ? "border-[var(--primary)] text-primary" : "border-border hover:text-foreground",
              )}
            >
              <Keyboard className="size-3" /> keys
            </button>
            {kbd && (
              <span className="tabular" title="Octave (Z / X)">
                C{octave} · {playheadBeats !== undefined ? "recording live" : `step at ${beats(cursor)}`}
                {midiInputs !== null && midiInputs > 0 ? ` · ${midiInputs} MIDI input${midiInputs === 1 ? "" : "s"}` : ""}
                {take.length > 0 ? ` · ${take.length} pending` : ""}
              </span>
            )}
          </span>
        )}
        <span className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
            disabled={zoom <= 1}
            onClick={() => setZoom((z) => Math.max(1, z / 1.5))}
            title="Zoom out (⌘ + scroll on the grid)"
            aria-label="Zoom out"
          >
            <ZoomOut className="size-3" />
          </button>
          <button
            type="button"
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
            disabled={zoom >= 32}
            onClick={() => setZoom((z) => Math.min(32, z * 1.5))}
            title="Zoom in (⌘ + scroll on the grid)"
            aria-label="Zoom in"
          >
            <ZoomIn className="size-3" />
          </button>
        </span>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            title="Minimize the piano roll"
            aria-label="Minimize the piano roll"
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className="size-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close the piano roll"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
  );

  const body = (
      omitted ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          This clip holds {clip.noteCount} notes — over the sync cap, so they didn't travel and
          can't be shown or edited here.
        </div>
      ) : steps ? (
        <StepGrid
          notes={notes}
          clipBeats={clip.duration}
          stepBeats={GRID}
          beatsPerBar={beatsPerBar}
          kit={kit}
          drumNames={drumNames}
          editable={editable}
          scale={scale ? { root: scaleRoot, intervals: scale.intervals, label: `${ROOTS[scaleRoot]} ${scale.label}` } : undefined}
          playheadBeats={playheadBeats}
          onChange={(next) => {
            // `next` was built over committed + take, so a typed take still
            // pending is already in it: absorb it rather than proposing it
            // first (two envelopes citing one rev — the second stale).
            if (flushTimer.current !== null) {
              window.clearTimeout(flushTimer.current);
              flushTimer.current = null;
            }
            takeRef.current = [];
            setTake([]);
            setSelected(new Set());
            commit(next);
          }}
          onAudition={onAudition}
        />
      ) : (
        <>
          {/* Ruler — outside the vertical scroll area so beat numbers stay put. */}
          <div
            ref={(el) => {
              stripRefs.current[0] = el;
            }}
            onScroll={syncScroll(0)}
            className="shrink-0 overflow-x-auto [scrollbar-width:none]"
            style={{ marginLeft: keysW }}
          >
            <div className="relative border-b border-border" style={{ height: RULER_H, width: `${zoom * 100}%` }}>
              {Array.from({ length: Math.floor(span / step) + 1 }, (_, i) => i * step).map((b) => (
                <span
                  key={b}
                  className="absolute top-0.5 pl-1 text-[10px] text-muted-foreground tabular"
                  style={{ left: pct(b) }}
                >
                  {barLabel(b)}
                </span>
              ))}
            </div>
          </div>

          <div
            ref={scrollRef}
            className={cn("overflow-y-auto", (fillBox || editor) && "min-h-[96px] flex-1")}
            style={fillBox || editor ? undefined : { maxHeight: rollHeight }}
          >
            <div className="flex">
              {/* A keyboard down the side: ivory, with the black keys short
                  over it, as Live draws it. Only Cs get a label — at 12px
                  rows anything more is noise. A kit is not a keyboard: as
                  with a Drum Rack in Live, every mapped row is a flat strip
                  carrying its drum's name (the black-key rows only a shade
                  darker, so the column still mirrors the grid's stripes). A
                  press sounds the key. */}
              <div className="shrink-0 border-r border-black/60 bg-[#d4d4da]" style={{ width: keysW }}>
                {rows.map((pitch) => (
                  <div
                    key={pitch}
                    title={keyTitle(pitch)}
                    onPointerDown={onAudition ? () => onAudition({ pitch, startTime: 0, duration: 0.5, velocity: 100 }) : undefined}
                    onClick={(e) => {
                      // ⇧-click: every note on this pitch (⌘⇧ adds them).
                      if (e.shiftKey) selectPitch(pitch, e.metaKey || e.ctrlKey);
                    }}
                    className={cn(
                      "relative flex items-center justify-end pr-1 text-[9px] font-semibold text-black/55 tabular",
                      onAudition && "cursor-pointer active:brightness-90",
                      // The line under each B and E: where two white keys meet.
                      (pitch % 12 === 0 || pitch % 12 === 5) && "border-b border-black/20",
                      kit && isBlackKey(pitch) && "bg-black/10",
                      outOfScale(pitch) && "opacity-50",
                    )}
                    style={{ height: ROW_H }}
                  >
                    {!kit && isBlackKey(pitch) && <span aria-hidden className="absolute inset-y-px left-0 w-[62%] rounded-r-sm bg-[#1b1b20]" />}
                    {keyLabel(pitch)}
                  </div>
                ))}
              </div>

              <div
                ref={(el) => {
                  stripRefs.current[1] = el;
                }}
                onScroll={syncScroll(1)}
                onWheel={(e) => {
                  if (!e.ctrlKey && !e.metaKey) return;
                  e.preventDefault();
                  setZoom((z) => Math.max(1, Math.min(32, z * (e.deltaY < 0 ? 1.5 : 1 / 1.5))));
                }}
                className="min-w-0 flex-1 overflow-x-auto"
              >
              <div
                ref={gridRef}
                onDoubleClick={drawMode || paintMode ? undefined : drawAt}
                onPointerDown={editable ? (drawMode ? beginDraw : paintMode ? beginPaint : beginBand) : undefined}
                onContextMenu={editable ? (e) => e.preventDefault() : undefined}
                onClick={(e) => {
                  if (suppressClick.current) {
                    suppressClick.current = false;
                    return;
                  }
                  if (e.target === e.currentTarget) setSelected(new Set());
                }}
                className={cn("relative", editable && (drawMode || paintMode ? "cursor-crosshair" : "cursor-cell"))}
                style={{ height: 128 * ROW_H, touchAction: drawMode || paintMode ? "none" : "pan-y", width: `${zoom * 100}%` }}
              >
                {/* Row shading mirrors the key column, so pitch reads across. */}
                {rows
                  .filter((p) => isBlackKey(p))
                  .map((pitch) => (
                    <div
                      key={pitch}
                      className="pointer-events-none absolute inset-x-0 bg-muted/50"
                      style={{ top: (127 - pitch) * ROW_H, height: ROW_H }}
                    />
                  ))}
                {scale &&
                  rows
                    .filter((p) => outOfScale(p))
                    .map((pitch) => (
                      <div
                        key={`scale-${pitch}`}
                        className="pointer-events-none absolute inset-x-0 bg-[color-mix(in_srgb,var(--removed)_9%,transparent)]"
                        style={{ top: (127 - pitch) * ROW_H, height: ROW_H }}
                      />
                    ))}
                {rows
                  .filter((p) => p % 12 === 0)
                  .map((pitch) => (
                    <div
                      key={`oct-${pitch}`}
                      className="pointer-events-none absolute inset-x-0 border-t border-border"
                      style={{ top: (127 - pitch) * ROW_H }}
                    />
                  ))}
                {Array.from({ length: Math.floor(span) + 1 }, (_, i) => i).map((b) => (
                  <div
                    key={`beat-${b}`}
                    className={cn(
                      "pointer-events-none absolute inset-y-0 w-px",
                      Math.abs(b / beatsPerBar - Math.round(b / beatsPerBar)) < 1e-6 ? "bg-border" : "bg-border/50",
                    )}
                    style={{ left: pct(b) }}
                  />
                ))}
                {clip.duration < span && (
                  <div
                    className="pointer-events-none absolute inset-y-0 right-0 bg-muted/60"
                    style={{ left: pct(clip.duration) }}
                  />
                )}
                {playheadBeats !== undefined && playheadBeats >= 0 && playheadBeats <= span && (
                  <div
                    className="pointer-events-none absolute inset-y-0 z-10 w-px"
                    style={{ left: pct(playheadBeats), backgroundColor: "var(--primary)" }}
                  />
                )}
                {kbd && playheadBeats === undefined && (
                  <div
                    className="pointer-events-none absolute inset-y-0 z-10 w-px bg-[var(--changed)]"
                    style={{ left: pct(cursor) }}
                    title="Step cursor"
                  />
                )}
                {band && (
                  <div
                    className="pointer-events-none absolute z-10 border border-[var(--primary)] bg-[color-mix(in_srgb,var(--primary)_15%,transparent)]"
                    style={{
                      left: pct(Math.min(band.b0, band.b1)),
                      width: pct(Math.abs(band.b1 - band.b0)),
                      top: (127 - Math.max(band.p0, band.p1)) * ROW_H,
                      height: (Math.abs(band.p1 - band.p0) + 1) * ROW_H,
                    }}
                  />
                )}

                {/* Ghost notes — the other MIDI lanes under this clip, dim and
                    untouchable, so a line is written against what sounds with it. */}
                {ghosts &&
                  ghostNotes?.map((g, i) => (
                    <div
                      key={`ghost-${i}`}
                      aria-hidden
                      title={`${g.laneName} · ${keyTitle(g.pitch)}`}
                      className="pointer-events-none absolute rounded-[2px] border border-dashed border-foreground/30 bg-foreground/15"
                      style={{
                        left: pct(g.startTime),
                        top: (127 - g.pitch) * ROW_H + 2,
                        width: `max(3px, ${(g.duration / span) * 100}%)`,
                        height: ROW_H - 4,
                      }}
                    />
                  ))}
                {shown.map((note) => {
                  const key = noteKey(note);
                  const isSelected = selected.has(key);
                  const typed = take.includes(note);
                  return (
                    <button
                      key={key}
                      type="button"
                      data-note-key={key}
                      onContextMenu={(e) => e.preventDefault()}
                      onMouseEnter={() => setHovered(note)}
                      onMouseLeave={() => setHovered((n) => (n === note ? null : n))}
                      onPointerDown={(e) => beginNoteDrag(e, note)}
                      onDoubleClick={(e) => {
                        // Double-click deletes; without this the second click's
                        // bubble would reach the grid and draw a replacement.
                        e.stopPropagation();
                        if (!editable) return;
                        commit(deleteNotes(notes, new Set([key])));
                        setSelected(new Set());
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (suppressClick.current) {
                          suppressClick.current = false;
                          return;
                        }
                        const additive = e.shiftKey || e.metaKey || e.ctrlKey;
                        setSelected((prev) => {
                          if (!additive) return new Set([key]);
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        });
                        if (!note.muted) onAudition?.(note);
                      }}
                      title={`${keyTitle(note.pitch)} · start ${beats(note.startTime)} · len ${beats(note.duration)} · vel ${Math.round(note.velocity)}`}
                      className={cn(
                        "absolute rounded-[2px] border border-black/40",
                        editable ? "cursor-grab" : "cursor-default",
                        (note.muted || typed) && "border-dashed",
                        isSelected && "ring-1 ring-[var(--primary)]",
                      )}
                      style={{
                        left: pct(note.startTime),
                        top: (127 - note.pitch) * ROW_H + 1,
                        width: `max(4px, ${(note.duration / span) * 100}%)`,
                        height: ROW_H - 2,
                        backgroundColor: fill,
                        opacity: note.muted ? 0.3 : 0.45 + (note.velocity / 127) * 0.55,
                      }}
                    >
                      {editable && <span className="absolute inset-y-0 right-0 w-1 cursor-col-resize" />}
                    </button>
                  );
                })}
              </div>
              </div>
            </div>
          </div>

          {/* The value lane — velocity, chance or deviation: one bar per note, drag vertically to set it. */}
          <div className="flex border-t border-border">
            <div className="flex shrink-0 flex-col justify-center gap-0.5 border-r border-border px-1" style={{ width: keysW }}>
              {LANE_KINDS.map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  onClick={() => setLaneKind(k.kind)}
                  title={k.title}
                  aria-pressed={laneKind === k.kind}
                  className={cn(
                    "rounded px-1 text-[9px] leading-4",
                    laneKind === k.kind ? "bg-[var(--primary)] text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {k.label}
                </button>
              ))}
            </div>
            <div
              ref={(el) => {
                stripRefs.current[2] = el;
              }}
              onScroll={syncScroll(2)}
              className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none]"
            >
              <div className="relative" style={{ height: VEL_H, width: `${zoom * 100}%` }}>
                {laneKind === "deviation" && <div className="absolute inset-x-0 border-t border-border/60" style={{ top: VEL_H / 2 }} />}
                {shown.map((note) => {
                  const key = noteKey(note);
                  const bar = laneBar(note, laneKind, VEL_H);
                  return (
                    <button
                      key={`lane-${key}`}
                      type="button"
                      onPointerDown={(e) => beginVelocityDrag(e, note)}
                      title={laneTitle(note, laneKind)}
                      className={cn(
                        "absolute w-1",
                        bar.fromTop ? "rounded-b-sm" : "rounded-t-sm",
                        editable ? "cursor-ns-resize" : "cursor-default",
                        selected.has(key) && "ring-1 ring-[var(--primary)]",
                      )}
                      style={{
                        left: pct(note.startTime),
                        ...(bar.fromTop ? { top: bar.at } : { bottom: bar.at }),
                        height: bar.height,
                        backgroundColor: fill,
                        opacity: note.muted ? 0.3 : 0.9,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          <div className={cn("flex min-h-8 shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground", editor?.presentation === "sheet" && !velDrag && "hidden")}>
            {velDrag ? (
              <span className="tabular">{laneReadout(velDrag.kind, velDrag.value)}</span>
            ) : hovered ? (
              <>
                <span className="font-medium text-foreground">{pitchName(hovered.pitch)}</span>
                <span className="tabular">start {beats(hovered.startTime)}</span>
                <span className="tabular">len {beats(hovered.duration)}</span>
                <span className="tabular">vel {Math.round(hovered.velocity)}</span>
                {hovered.probability !== undefined && hovered.probability < 1 && (
                  <span className="tabular">chance {Math.round(hovered.probability * 100)}%</span>
                )}
                {hovered.velocityDeviation !== undefined && hovered.velocityDeviation !== 0 && (
                  <span className="tabular">dev {hovered.velocityDeviation > 0 ? "+" : ""}{hovered.velocityDeviation}</span>
                )}
                {hovered.muted && <Badge variant="removed">muted</Badge>}
              </>
            ) : (
              <span>
                {editable
                  ? "dbl-click draws · right-click erases · drag moves, edge resizes · ⌥-drag clones · ↑↓ ←→ move · M mute · ⌘D repeat"
                  : "click a note to hear it"}
              </span>
            )}
          </div>
        </>
      )
  );

  // The room's frame: the same strip, tabs and controls the audio clip
  // editor has (clip-editor/chrome.tsx), the grid where its waveform is.
  const sheet = editor?.presentation === "sheet";
  const tool: Tool = steps ? "steps" : paintMode ? "paint" : drawMode ? "draw" : "select";
  const setTool = (t: Tool) => {
    if (t === "steps") {
      setStepsOverride(true);
      if (!kit) setStepsPref(true);
      return;
    }
    if (steps) {
      setStepsOverride(false);
      if (!kit) setStepsPref(false);
    }
    setDrawModeState(t === "draw");
    setPaintModeState(t === "paint");
  };
  const chordNow = CHORDS.find((c) => c.id === chordId) ?? null;
  const target = selected.size > 0 ? `the ${selected.size} selected note${selected.size === 1 ? "" : "s"}` : "every note";
  const editorTabs: ReadonlyArray<{ value: EditorTab; label: string }> = [
    { value: "notes", label: "Notes" },
    { value: "edit", label: "Edit" },
    ...(kit ? [] : [{ value: "scale" as const, label: "Scale" }]),
    ...(editor?.clipPanel ? [{ value: "clip" as const, label: "Clip" }] : []),
  ];
  const tabNow: EditorTab = (editorTab === "clip" && !editor?.clipPanel) || (editorTab === "scale" && kit) ? "notes" : editorTab;
  const editorPanel = (() => {
    if (!editable && tabNow !== "clip") return <p className="text-[11px] text-muted-foreground">Read-only.</p>;
    if (omitted && tabNow !== "clip") return null;
    switch (tabNow) {
      case "notes":
        return (
          <div className="flex flex-col gap-4">
            <Field
              label="Tool"
              hint={
                tool === "draw"
                  ? "Press on empty grid to draw a note as long as the last one; drag right for its length. Right-click erases. (B)"
                  : tool === "paint"
                    ? "Sweep across the grid to lay a note in every cell you cross, each as long as the last note drawn. Right-click erases. (P)"
                    : tool === "steps"
                      ? "A drum machine over the clip: tap a cell for a hit, tap again to clear it, drag to paint a run, drag a hit to move it, ⇧-tap to accent."
                      : "Drag to move, the right edge to resize; ⌥-drag clones; double-click draws; right-click erases."
              }
            >
              <BigChoice
                value={tool}
                onChange={setTool}
                options={[
                  { value: "select", label: "Select" },
                  { value: "draw", label: "Draw" },
                  { value: "paint", label: "Paint" },
                  { value: "steps", label: "Steps" },
                ]}
              />
            </Field>
            {tool !== "steps" && (
              <Field label="Stamp" hint={chordNow ? `A draw places a ${chordNow.label} chord on the pressed note.` : "A draw places one note; pick a chord to stamp it instead."}>
                <select
                  value={chordId}
                  onChange={(e) => setChordId(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  aria-label="Chord stamp"
                  className="h-11 rounded-lg bg-panel px-3 text-sm font-semibold outline-none ring-1 ring-inset ring-panelline wide:h-8 wide:text-xs"
                >
                  <option value="">Single note</option>
                  {CHORDS.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {ghostNotes && (
              <Field label="Ghosts" hint={ghosts ? `The other MIDI lanes' notes under this clip, dimmed${ghostNotes.length === 0 ? " — none sound here" : ""}.` : "Show the other MIDI lanes' notes under this clip."}>
                <PadRow cols={1}>
                  <Pad label="Ghost notes from the other MIDI lanes" active={ghosts} onClick={() => setGhosts(!ghosts)}>
                    <Ghost /> {ghosts ? "Ghost notes on" : "Ghost notes"}
                  </Pad>
                </PadRow>
              </Field>
            )}
            <Field label="Grid">
              <PadRow cols={3}>
                {GRID_CHOICES.map((g) => (
                  <Pad key={g.label} label={`Grid ${g.label}`} active={Math.abs(GRID - g.value) < 1e-9} onClick={() => setGrid(g.value)}>
                    {g.label}
                  </Pad>
                ))}
              </PadRow>
            </Field>
            {onScaleLoop && (
              <Field label="Length" hint={`${clip.duration / beatsPerBar >= 1 ? `${Math.round((clip.duration / beatsPerBar) * 10) / 10} bars` : `${beats(clip.duration)} beats`}. ×2 repeats the notes into the new half.`}>
                <PadRow cols={2}>
                  <Pad label="Halve the clip, keeping its first half" onClick={() => onScaleLoop(0.5)}>
                    ÷2
                  </Pad>
                  <Pad label="Double the clip, its notes repeating" onClick={() => onScaleLoop(2)}>
                    ×2
                  </Pad>
                </PadRow>
              </Field>
            )}
            <Field
              label="Play it in"
              hint={
                kbd
                  ? `A W S E D F T G Y H U J K play, Z / X the octave (C${octave}). ${playheadBeats !== undefined ? "Recording where the playhead is." : `Steps in at ${beats(cursor)}.`}${take.length > 0 ? ` ${take.length} pending.` : ""}`
                  : "Play notes on the computer keyboard (or a MIDI keyboard), step by step or over the transport."
              }
            >
              <PadRow cols={1}>
                <Pad label="Computer keyboard input" active={kbd} onClick={() => setKbd((k) => !k)}>
                  <Keyboard /> {kbd ? "Keys on" : "Computer keys"}
                  {midiInputs !== null && midiInputs > 0 ? ` · ${midiInputs} MIDI` : ""}
                </Pad>
              </PadRow>
            </Field>
            {!sheet && (
              <Field label="Zoom" hint="Or ⌘ + scroll on the grid.">
                <PadRow cols={2}>
                  <Pad label="Zoom out" disabled={zoom <= 1} onClick={() => setZoom((z) => Math.max(1, z / 1.5))}>
                    <ZoomOut />
                  </Pad>
                  <Pad label="Zoom in" disabled={zoom >= 32} onClick={() => setZoom((z) => Math.min(32, z * 1.5))}>
                    <ZoomIn />
                  </Pad>
                </PadRow>
              </Field>
            )}
          </div>
        );
      case "edit":
        return (
          <div className="flex flex-col gap-4">
            <p className="text-[11px] leading-snug text-muted-foreground">Applies to {target}.</p>
            <Field label="Quantize">
              <PadRow cols={4}>
                {([[1, "1/4"], [0.5, "1/8"], [0.25, "1/16"], [0.125, "1/32"]] as const).map(([g, label]) => (
                  <Pad key={label} label={`Quantize to ${label}`} onClick={() => quantize(g)}>
                    {label}
                  </Pad>
                ))}
              </PadRow>
            </Field>
            <div className="flex flex-wrap gap-x-4 gap-y-3">
              <Field label="Strength" hint="How far notes move.">
                <Stepper value={Math.round(groove.strength * 100)} step={10} min={0} max={100} unit="%" label="Quantize strength" onCommit={(v) => setGroove({ ...groove, strength: v / 100 })} />
              </Field>
              <Field label="Swing" hint="Every second step, late.">
                <Stepper value={Math.round(groove.swing * 100)} step={10} min={0} max={100} unit="%" label="Swing" onCommit={(v) => setGroove({ ...groove, swing: v / 100 })} />
              </Field>
            </div>
            <Field label="Transform">
              <PadRow cols={3}>
                <Pad label="Humanize — nudge timing by up to a 64th and velocity by up to ±10" onClick={humanize}>Humanize</Pad>
                <Pad label="Legato — stretch each note to the next note's start" onClick={legato}>Legato</Pad>
                <Pad label="Velocity ramp — from the earliest selected note's velocity to the latest's" disabled={selected.size < 2} onClick={ramp}>Ramp</Pad>
                <Pad label="Chop — cut each note into grid-length repeats" onClick={() => transform(chopNotes(notes, selected, GRID), false)}>Chop</Pad>
                <Pad label="Arpeggiate — chords become runs at the grid rate" onClick={() => transform(arpeggiateNotes(notes, selected, GRID), false)}>Arp</Pad>
                <Pad label="Reverse — mirror in time" onClick={() => transform(reverseNotes(notes, selected), true)}>Reverse</Pad>
                <Pad label="Glue (⌘G) — join touching notes on a pitch into one" onClick={() => transform(glueNotes(notes, selected), false)}>Glue</Pad>
                <Pad label="Flip — mirror pitches within the range, top note to the bottom" onClick={() => transform(flipNotes(notes, selected), true)}>Flip</Pad>
                <Pad label="Strum — spread each chord's notes a 64th apart, up from the bottom" onClick={() => transform(strumNotes(notes, selected, STRUM_BEATS), true)}>Strum</Pad>
                <Pad label="Flam — a quieter grace note a 64th before each note" onClick={() => transform(flamNotes(notes, selected, FLAM_BEATS), false)}>Flam</Pad>
              </PadRow>
            </Field>
            <Field label="Levels" hint="Velocities scaled, dynamics kept (the lane sets one value).">
              <PadRow cols={2}>
                <Pad label="Softer — velocities × 0.8" onClick={() => transform(scaleVelocity(notes, selected, 0.8), true)}>Softer</Pad>
                <Pad label="Louder — velocities × 1.25" onClick={() => transform(scaleVelocity(notes, selected, 1.25), true)}>Louder</Pad>
              </PadRow>
            </Field>
            <Field label="Lengths" hint="Starts stay; a stretched note overwrites what it reaches.">
              <PadRow cols={2}>
                <Pad label="Staccato — lengths halved" onClick={() => transform(scaleLength(notes, selected, 0.5, clip.duration), false)}>½ length</Pad>
                <Pad label="Stretch — lengths doubled, cut at the clip" onClick={() => transform(scaleLength(notes, selected, 2, clip.duration), false)}>×2 length</Pad>
              </PadRow>
            </Field>
          </div>
        );
      case "scale":
        return (
          <div className="flex flex-col gap-4">
            {setScale ? (
              <Field label="The Set's scale" hint="As Live reports it. Rows outside it are tinted; change it in Live.">
                <div data-testid="room-scale" className="flex h-11 items-center rounded-lg bg-panel px-3 text-sm font-semibold ring-1 ring-inset ring-panelline wide:h-8 wide:text-xs">
                  {ROOTS[setScale.root]} {setScale.name}
                </div>
              </Field>
            ) : (
              <>
                <Field label="Root" hint={roomScale && !roomScale.mode ? `Live's Scale Mode is off (the Set says ${ROOTS[roomScale.root]} ${roomScale.name}): this is a drawing aid in this browser.` : "A drawing aid in this browser, not the Set's key."}>
                  <PadRow cols={6}>
                    {ROOTS.map((r, i) => (
                      <Pad key={r} label={`Root ${r}`} active={scaleChoice.root === i} onClick={() => setScaleChoice({ ...scaleChoice, root: i })}>
                        {r}
                      </Pad>
                    ))}
                  </PadRow>
                </Field>
                <Field label="Scale">
                  <select
                    value={scaleChoice.scaleId}
                    onChange={(e) => setScaleChoice({ ...scaleChoice, scaleId: e.target.value })}
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label="Scale"
                    className="h-11 rounded-lg bg-panel px-3 text-sm font-semibold outline-none ring-1 ring-inset ring-panelline wide:h-8 wide:text-xs"
                  >
                    <option value="">No scale</option>
                    {SCALES.map((sc) => (
                      <option key={sc.id} value={sc.id}>
                        {sc.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            )}
            {scale && (
              <Field label="Fold" hint={`Moves ${target} onto ${ROOTS[scaleRoot]} ${scale.label}.`}>
                <PadRow cols={1}>
                  <Pad label={`Fold onto ${ROOTS[scaleRoot]} ${scale.label}`} onClick={fold}>
                    Fold to scale
                  </Pad>
                </PadRow>
              </Field>
            )}
          </div>
        );
      case "clip":
        return editor?.clipPanel ?? null;
    }
  })();
  const tabs = editor ? (
    <div className={cn(sheet && "z-10 border-b border-panelline bg-sunk/95 backdrop-blur")}>
      <EditorTabs tabs={editorTabs} value={tabNow} onChange={setEditorTab} column={sheet} label="MIDI clip settings" />
    </div>
  ) : null;
  const editorLayout = editor ? (
    <>
      {editor.header}
      {sheet && tabs}
      <div className={cn("flex min-h-0 flex-1", sheet ? "flex-col overflow-y-auto overscroll-contain" : "flex-row-reverse")}>
        <div className={cn("flex min-w-0 flex-col", sheet ? "min-h-[220px] flex-1" : "min-h-0 flex-1")}>
          {pending && <div className="shrink-0 px-3 py-1 text-[11px] text-muted-foreground">syncing…</div>}
          {body}
        </div>
        <div className={cn("flex shrink-0 flex-col", sheet ? "border-t border-panelline" : "min-h-0 w-[300px] border-r border-panelline")}>
          {!sheet && tabs}
          <div role="tabpanel" className={cn("px-3 text-xs", sheet ? "py-4" : "min-h-0 overflow-y-auto py-2.5")}>
            {editorPanel}
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <div
      data-piano-roll
      tabIndex={-1}
      onKeyUp={keyUp}
      onBlur={(e) => {
        // Focus leaving the roll ends any held keys and lands the take.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          held.current.clear();
          flushTake();
        }
      }}
      onKeyDown={(e) => {
        // These are the roll's own shortcuts — the page's clip shortcuts
        // (which also bind Delete) must not fire for the same keystroke.
        if (keyDown(e)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          e.stopPropagation();
          removeSelection();
        } else if (e.key === "Escape") {
          e.stopPropagation();
          if (selected.size > 0) setSelected(new Set());
          else onClose();
        } else if ((e.key === "ArrowUp" || e.key === "ArrowDown") && selected.size > 0) {
          e.preventDefault();
          e.stopPropagation();
          transpose((e.key === "ArrowUp" ? 1 : -1) * (e.shiftKey ? 12 : 1));
        } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && selected.size > 0) {
          // Nudge by the grid; ⇧ by a bar (with keys on, plain arrows move the step cursor instead).
          e.preventDefault();
          e.stopPropagation();
          nudge((e.key === "ArrowRight" ? 1 : -1) * (e.shiftKey ? beatsPerBar : GRID));
        } else if (!(e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b" && !kbd && editable) {
          e.preventDefault();
          e.stopPropagation();
          setDrawMode((d) => !d);
        } else if (!(e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "p" && !kbd && editable) {
          e.preventDefault();
          e.stopPropagation();
          setPaintMode((d) => !d);
        } else if (!(e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "m") {
          e.stopPropagation();
          muteSelection();
        } else if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === "d" || e.key.toLowerCase() === "b")) {
          // Live's ⌘D and FL's ⌘B: repeat the selection after its span.
          e.preventDefault();
          e.stopPropagation();
          duplicateSelection();
        } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "c") {
          e.preventDefault();
          e.stopPropagation();
          copySelection(false);
        } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "x") {
          e.preventDefault();
          e.stopPropagation();
          copySelection(true);
        } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "v") {
          e.preventDefault();
          e.stopPropagation();
          pasteClipboard();
        } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "g") {
          e.preventDefault();
          e.stopPropagation();
          transform(glueNotes(notes, selected), false);
        } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a") {
          e.preventDefault();
          e.stopPropagation();
          setSelected(new Set(notes.map(noteKey)));
        }
      }}
      className={cn(
        editor ? "flex min-h-0 flex-1 flex-col bg-sunk outline-none" : "rounded-lg border border-border bg-card outline-none",
        !editor && fillBox && "flex min-h-0 flex-1 flex-col",
        className,
      )}
    >
      {editor ? editorLayout : (
        <>
          {toolbar}
          {body}
        </>
      )}
    </div>
  );
}

/** Module-level, like the guard: `usePref` re-reads whenever its fallback
 * changes identity, so an inline object here would re-read every render. */
const DEFAULT_GROOVE: Groove = { strength: 1, swing: 0 };

function isGroove(v: unknown): v is Groove {
  if (typeof v !== "object" || v === null) return false;
  const g = v as Record<string, unknown>;
  return typeof g.strength === "number" && g.strength >= 0 && g.strength <= 1 && typeof g.swing === "number" && g.swing >= 0 && g.swing <= 1;
}

/** A 0..1 value shown and typed as a percentage; committed on blur/Enter. */
function PercentField({ label, title, value, onCommit }: { label: string; title: string; value: number; onCommit: (v: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = Number(draft);
    setDraft(null);
    if (draft.trim() === "" || !Number.isFinite(n)) return;
    onCommit(Math.max(0, Math.min(100, Math.round(n))) / 100);
  };
  return (
    <label className="flex items-center gap-0.5" title={title}>
      <span>{label}</span>
      <input
        type="number"
        min={0}
        max={100}
        step={5}
        value={draft ?? String(Math.round(value * 100))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setDraft(null);
        }}
        aria-label={`${label} (%)`}
        className="tabular h-6 w-12 rounded-md border border-input bg-transparent px-1 text-[11px]"
      />
      <span>%</span>
    </label>
  );
}

type Tool = "select" | "draw" | "paint" | "steps";

function isString(v: unknown): v is string {
  return typeof v === "string";
}

function isLaneKind(v: unknown): v is LaneKind {
  return v === "velocity" || v === "chance" || v === "deviation";
}

/** A note's value in a lane. */
function laneValue(n: WireNote, kind: LaneKind): number {
  if (kind === "velocity") return n.velocity;
  if (kind === "chance") return n.probability ?? 1;
  return n.velocityDeviation ?? 0;
}

/** The keyed notes with a lane value set. */
function applyLaneValue(notes: readonly WireNote[], keys: ReadonlySet<string>, kind: LaneKind, value: number): WireNote[] {
  if (kind === "velocity") return setNotesVelocity(notes, keys, value);
  if (kind === "chance") return setNotesProbability(notes, keys, value);
  return setNotesDeviation(notes, keys, value);
}

/** Where a lane bar sits: velocity and chance rise from the bottom; a
 * deviation rises from the middle (positive) or hangs from it (negative). */
function laneBar(n: WireNote, kind: LaneKind, laneHeight: number): { at: number; height: number; fromTop: boolean } {
  if (kind === "velocity") return { at: 0, height: Math.max(2, (n.velocity / 127) * laneHeight), fromTop: false };
  if (kind === "chance") return { at: 0, height: Math.max(2, (n.probability ?? 1) * laneHeight), fromTop: false };
  const d = n.velocityDeviation ?? 0;
  const half = laneHeight / 2;
  if (d >= 0) return { at: half, height: Math.max(2, (d / 127) * half), fromTop: false };
  return { at: half, height: Math.max(2, (-d / 127) * half), fromTop: true };
}

function laneReadout(kind: LaneKind, value: number): string {
  if (kind === "velocity") return `velocity ${value}`;
  if (kind === "chance") return `chance ${Math.round(value * 100)}%`;
  return `deviation ${value > 0 ? "+" : ""}${value}`;
}

function laneTitle(n: WireNote, kind: LaneKind): string {
  return laneReadout(kind, laneValue(n, kind));
}
